import { hasLocalePrefix } from '@/router';
import { truncateString } from '@worldcoo/shared/utils';
import { useEffect } from 'react';

interface HreflangAlternate {
	hreflang: string;
	href: string;
}

/** Map short locale codes to BCP 47 og:locale values. */
const localeToOgLocale: Record<string, string> = {
	es: 'es_ES',
	en: 'en_US',
	ca: 'ca_ES',
	it: 'it_IT',
	de: 'de_DE',
	fr: 'fr_FR',
	pt: 'pt_PT',
	nl: 'nl_NL',
	eu: 'eu_ES',
	gl: 'gl_ES',
};

/**
 * Sets all SEO head elements for a page: meta tags, Open Graph, Twitter, canonical,
 * robots, hreflang alternates, and JSON-LD structured data.
 *
 * **Rendering strategy:**
 * - Title, meta, canonical: rendered as JSX so React 19 Float hoists them to <head>
 *   during SSR, then re-applied via DOM after hydration so they survive React Float's
 *   cleanup when Suspense boundaries replay.
 * - Hreflang and JSON-LD: managed exclusively via the DOM API (never JSX). React 19
 *   Float hoists <link rel="alternate"> and <script type="application/ld+json"> to
 *   <head> on every Suspense replay without deduplication, causing triplication in the
 *   live DOM. The DOM-only approach with `data-seo-managed` deduplication is idempotent
 *   regardless of how many times the Suspense boundary fires.
 */
export const Seo = (props: {
	title?: string;
	description?: string;
	image?: string;
	imageWidth?: number;
	imageHeight?: number;
	/** MIME type of the og:image (e.g. 'image/jpeg', 'image/webp') */
	imageType?: string;
	/** Alt text for og:image and twitter:image. Falls back to title when not provided. */
	imageAlt?: string;
	canonicalUrl?: string;
	/** Current locale code (e.g., 'es') — used for og:locale */
	locale?: string;
	/** Other available locale codes — used for og:locale:alternate */
	alternateLocales?: string[];
	/** Site name shown in og:site_name (e.g., portal or merchant name) */
	siteName?: string;
	/** When true, renders <meta name="robots" content="noindex"> */
	noindex?: boolean;
	/** Override og:type (default: 'website'). */
	ogType?: string;
	/** Article-specific Open Graph metadata (only used when ogType='article'). */
	articleMeta?: {
		publishedTime?: string;
		tags?: string[];
	};
	/** Hreflang alternate links. */
	alternates?: HreflangAlternate[];
	/** JSON-LD structured data schemas. */
	jsonLd?: Record<string, unknown>[];
}) => {
	// Cap meta description at ~160 chars (Google's display limit) at a word boundary.
	const description = props.description ? truncateString(props.description, 160) : undefined;
	const ogLocale = props.locale
		? (localeToOgLocale[props.locale] ?? `${props.locale}_${props.locale.toUpperCase()}`)
		: undefined;
	const ogAlternateLocales = props.alternateLocales
		?.filter((l) => l !== props.locale)
		.map((l) => localeToOgLocale[l])
		.filter((v): v is string => Boolean(v));

	useEffect(() => {
		// Meta tags — re-applied after hydration to survive React 19 Float cleanup during
		// Suspense boundary replay.
		if (props.title) document.title = props.title;
		setMeta('name', 'description', description);
		setMeta('name', 'robots', props.noindex ? 'noindex' : undefined);
		setMeta('name', 'twitter:title', props.title);
		setMeta('name', 'twitter:description', description);
		setMeta('name', 'twitter:creator', '@worldcoo');
		setMeta('name', 'twitter:site', '@worldcoo');
		setMeta('property', 'og:type', props.ogType ?? 'website');
		setMeta('property', 'og:title', props.title);
		setMeta('property', 'og:description', description);
		setMeta('property', 'og:locale', ogLocale);
		setMeta('property', 'og:site_name', props.siteName);
		setMeta('property', 'og:url', props.canonicalUrl);
		setMeta('property', 'article:published_time', props.articleMeta?.publishedTime);
		setMeta('name', 'twitter:image', props.image);
		setMeta('name', 'twitter:image:alt', props.image ? (props.imageAlt ?? props.title) : undefined);
		setMeta('name', 'twitter:card', props.image ? 'summary_large_image' : undefined);
		setMeta('property', 'og:image', props.image);
		setMeta('property', 'og:image:width', props.image && props.imageWidth ? String(props.imageWidth) : undefined);
		setMeta('property', 'og:image:height', props.image && props.imageHeight ? String(props.imageHeight) : undefined);
		setMeta('property', 'og:image:type', props.image ? props.imageType : undefined);
		setMeta('property', 'og:image:alt', props.image ? (props.imageAlt ?? props.title) : undefined);
		syncMultiMeta('property', 'article:tag', props.articleMeta?.tags);
		syncMultiMeta('property', 'og:locale:alternate', ogAlternateLocales);

		// Hreflang and JSON-LD — managed exclusively via DOM API, never as JSX.
		syncHreflang(props.alternates);
		syncJsonLd(props.jsonLd);

		return () => {
			syncHreflang(undefined);
			syncJsonLd(undefined);
		};
	});

	// SSR: React Float hoists <title>, <meta>, and <link rel="canonical"> to <head>.
	return (
		<>
			{props.title && <title>{props.title}</title>}
			{description && <meta name="description" content={description} />}
			{props.noindex && <meta name="robots" content="noindex" />}
			{props.canonicalUrl && <link rel="canonical" href={props.canonicalUrl} />}
			<meta name="twitter:title" content={props.title} />
			{description && <meta name="twitter:description" content={description} />}
			<meta name="twitter:creator" content="@worldcoo" />
			<meta name="twitter:site" content="@worldcoo" />
			<meta property="og:type" content={props.ogType ?? 'website'} />
			{props.articleMeta?.publishedTime && (
				<meta property="article:published_time" content={props.articleMeta.publishedTime} />
			)}
			{props.articleMeta?.tags?.map((tag) => (
				<meta key={tag} property="article:tag" content={tag} />
			))}
			<meta property="og:title" content={props.title} />
			{ogLocale && <meta property="og:locale" content={ogLocale} />}
			{props.alternateLocales
				?.filter((l) => l !== props.locale)
				.map((l) => localeToOgLocale[l])
				.filter(Boolean)
				.map((ogLoc) => (
					<meta key={ogLoc} property="og:locale:alternate" content={ogLoc} />
				))}
			{props.siteName && <meta property="og:site_name" content={props.siteName} />}
			{props.canonicalUrl && <meta property="og:url" content={props.canonicalUrl} />}
			{description && <meta property="og:description" content={description} />}
			{props.image && (
				<>
					<meta name="twitter:image" content={props.image} />
					<meta name="twitter:image:alt" content={props.imageAlt ?? props.title} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta property="og:image" content={props.image} />
					{!!props.imageWidth && <meta property="og:image:width" content={String(props.imageWidth)} />}
					{!!props.imageHeight && <meta property="og:image:height" content={String(props.imageHeight)} />}
					{!!props.imageType && <meta property="og:image:type" content={props.imageType} />}
					<meta property="og:image:alt" content={props.imageAlt ?? props.title} />
				</>
			)}
		</>
	);
};

/**
 * Build hreflang alternate links where each locale may have a different path segment.
 * Used for campaign pages where slugs differ per language.
 * Falls back to `fallbackId` (UUID / route name) for locales without a slug so each
 * emitted URL still resolves via the app's slug-or-id lookup.
 * x-default points to the pathless URL using the first locale's slug. There are no
 * redirects at the root — the pathless URL is self-canonical (session locale stays
 * unset), so `${baseUrl}/${firstLocaleSlug}` is the correct x-default.
 */
export function buildAlternatesWithSlugs(
	baseUrl: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
	fallbackId: string,
): HreflangAlternate[] {
	const alternates: HreflangAlternate[] = locales.map((locale) => {
		const slug = slugsByLocale[locale] ?? fallbackId;
		return { hreflang: locale, href: `${baseUrl}/${locale}/${slug}` };
	});
	const defaultLocale = locales[0];
	if (defaultLocale) {
		const defaultSlug = slugsByLocale[defaultLocale] ?? fallbackId;
		// Pathless URL (no locale prefix) is self-canonical: session defaults to the first
		// portal locale, so canonical = ${baseUrl}/${defaultSlug} which equals this URL.
		alternates.push({ hreflang: 'x-default', href: `${baseUrl}/${defaultSlug}` });
	}
	return alternates;
}

/**
 * Build hreflang alternate links for all supported locales + x-default.
 * x-default points to the pathless root URL (no locale prefix). There are no redirects
 * at the root — visiting the bare domain keeps the session locale-free, so the pathless
 * URL is self-canonical and is the correct x-default signal for Google.
 */
export function buildAlternates(baseUrl: string, path: string, locales: readonly string[]): HreflangAlternate[] {
	const cleanPath = path.startsWith('/') ? path : `/${path}`;
	const pathSuffix = cleanPath === '/' ? '' : cleanPath;
	const alternates: HreflangAlternate[] = locales.map((locale) => ({
		hreflang: locale,
		href: `${baseUrl}/${locale}${pathSuffix}`,
	}));
	// Pathless URL has no locale prefix and its canonical is ${baseUrl}${pathSuffix}
	// (hasLocalePrefix() returns false → canonical = baseUrl/path). Self-canonical ✓
	alternates.push({ hreflang: 'x-default', href: `${baseUrl}${pathSuffix}` });
	return alternates;
}

/**
 * Resolve slug + canonical + alternates for a route whose slug varies per locale.
 * The canonical matches the page's actual URL: at `/${slug}` (unprefixed root) it's
 * `${baseUrl}/${slug}`; at `/${locale}/${slug}` it's the prefixed form. Lighthouse
 * flags the canonical as invalid when it matches a non-self hreflang alternate.
 */
export function resolveSlugSeo(
	baseUrl: string,
	locale: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
) {
	const slug = slugsByLocale[locale] ?? slugsByLocale.en!;
	return {
		slug,
		canonicalUrl: hasLocalePrefix() ? `${baseUrl}/${locale}/${slug}` : `${baseUrl}/${slug}`,
		alternates: buildAlternatesWithSlugs(baseUrl, locales, slugsByLocale, slug),
	};
}

// Only operate on nodes this module has created. Nodes hoisted into <head> by React 19's
// Float (from JSX like <meta>, <link>, <script>) are tracked in React's fiber tree — if
// we remove or move them, a later unmount triggers `Cannot read properties of null
// (reading 'removeChild')` because the node's parentNode is gone.
const MANAGED_ATTR = 'data-seo-managed';
const MANAGED_SELECTOR = `[${MANAGED_ATTR}="true"]`;

function setMeta(attr: 'name' | 'property', key: string, content: string | undefined) {
	const existing = document.head.querySelector(`meta[${attr}="${key}"]${MANAGED_SELECTOR}`);
	if (!content) {
		existing?.remove();
		return;
	}
	if (existing) {
		existing.setAttribute('content', content);
		return;
	}
	const el = document.createElement('meta');
	el.setAttribute(attr, key);
	el.setAttribute('content', content);
	el.setAttribute(MANAGED_ATTR, 'true');
	document.head.appendChild(el);
}

function syncMultiMeta(attr: 'name' | 'property', key: string, values: string[] | undefined) {
	for (const el of document.head.querySelectorAll(`meta[${attr}="${key}"]${MANAGED_SELECTOR}`)) el.remove();
	if (!values) return;
	for (const value of values) {
		const el = document.createElement('meta');
		el.setAttribute(attr, key);
		el.setAttribute('content', value);
		el.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(el);
	}
}

function syncHreflang(alternates: HreflangAlternate[] | undefined) {
	for (const el of document.head.querySelectorAll(`link[rel="alternate"]${MANAGED_SELECTOR}`)) el.remove();
	if (!alternates) return;
	for (const alt of alternates) {
		const link = document.createElement('link');
		link.rel = 'alternate';
		link.hreflang = alt.hreflang;
		link.href = alt.href;
		link.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(link);
	}
}

function syncJsonLd(schemas: Record<string, unknown>[] | undefined) {
	for (const el of document.head.querySelectorAll(`script[type="application/ld+json"]${MANAGED_SELECTOR}`)) el.remove();
	if (!schemas) return;
	for (const schema of schemas) {
		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.textContent = JSON.stringify(schema);
		script.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(script);
	}
}
